import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiMenu, FiLogOut, FiChevronDown, FiSettings, FiActivity, FiKey, FiEye, FiEyeOff } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { confirmDialog } from '../../utils/alert';
import { titleCase } from '../../utils/format';
import Modal from '../common/Modal';
import { authApi } from '../../api/auth';
import { toast, showError } from '../../utils/alert';
import { extractError } from '../../api/client';

export default function Header({ onToggleSidebar }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pwModal, setPwModal] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: '', password: '', password_confirmation: '' });
  const [pwErrors, setPwErrors] = useState({});
  const [pwSaving, setPwSaving] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = async () => {
    const res = await confirmDialog({
      title: 'Logout?',
      text: 'You will be signed out of the system.',
      confirmText: 'Yes, logout',
    });
    if (res.isConfirmed) {
      await logout();
      navigate('/login');
    }
  };

  const handleChangePassword = async () => {
    const errs = {};
    if (!pwForm.current_password) errs.current_password = 'Current password is required';
    if (!pwForm.password) errs.password = 'New password is required';
    else if (pwForm.password.length < 8) errs.password = 'Min 8 characters';
    if (pwForm.password !== pwForm.password_confirmation) errs.password_confirmation = 'Passwords do not match';
    if (Object.keys(errs).length) { setPwErrors(errs); return; }

    setPwSaving(true);
    try {
      await authApi.changePassword(pwForm);
      toast('success', '', 'Password changed. Please login again.');
      setPwModal(false);
      setPwForm({ current_password: '', password: '', password_confirmation: '' });
      setPwErrors({});
      await logout();
      navigate('/login');
    } catch (err) {
      showError('Failed', extractError(err));
    } finally {
      setPwSaving(false);
    }
  };

  const initials = (user && user.name)
    ? user.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()
    : 'AC';
  const role = user && user.roles && user.roles.length
    ? user.roles.map((r) => r.name || r).join(', ')
    : 'Administrator';

  return (
    <header className="topbar">
      <div className="topbar-left">
        <button className="btn-icon topbar-toggle" onClick={onToggleSidebar}>
          <FiMenu />
        </button>
      </div>
      <div className="topbar-right">
        <div className="user-menu" ref={menuRef} onClick={() => setMenuOpen((v) => !v)}>
          <div className="user-avatar">{initials}</div>
          <div className="user-meta">
            <div className="user-name">{user ? user.name : 'Admin'}</div>
            <div className="user-role">{titleCase(role)}</div>
          </div>
          <FiChevronDown style={{ color: 'var(--text-muted)' }} />
          {menuOpen && (
            <div className="dropdown-menu">
              <button className="dropdown-item" onClick={() => { setMenuOpen(false); navigate('/activity-logs'); }}>
                <FiActivity /> Activity Logs
              </button>
              <button className="dropdown-item" onClick={() => { setMenuOpen(false); navigate('/settings'); }}>
                <FiSettings /> Settings
              </button>
              <button className="dropdown-item" onClick={() => { setMenuOpen(false); setPwModal(true); }}>
                <FiKey /> Change Password
              </button>
              <div className="dropdown-divider" />
              <button className="dropdown-item danger" onClick={handleLogout}>
                <FiLogOut /> Logout
              </button>
            </div>
          )}
        </div>
      </div>

      <Modal
        open={pwModal}
        onClose={() => setPwModal(false)}
        title="Change Password"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setPwModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleChangePassword} disabled={pwSaving}>
              {pwSaving ? 'Saving...' : 'Update Password'}
            </button>
          </>
        }
      >
        <div className="form-group">
          <label className="form-label">Current Password <span className="req">*</span></label>
          <div style={{ position: 'relative' }}>
            <input
              type={showCurrent ? 'text' : 'password'}
              className={`form-control ${pwErrors.current_password ? 'is-invalid' : ''}`}
              value={pwForm.current_password}
              onChange={(e) => { setPwForm({ ...pwForm, current_password: e.target.value }); if (pwErrors.current_password) setPwErrors((p) => ({ ...p, current_password: '' })); }}
              placeholder="Enter current password"
              style={{ paddingRight: 40 }}
            />
            <button
              type="button"
              onClick={() => setShowCurrent((v) => !v)}
              style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}
            >
              {showCurrent ? <FiEyeOff size={16} /> : <FiEye size={16} />}
            </button>
          </div>
          {pwErrors.current_password && <div className="invalid-feedback">{pwErrors.current_password}</div>}
        </div>
        <div className="form-group">
          <label className="form-label">New Password <span className="req">*</span></label>
          <div style={{ position: 'relative' }}>
            <input
              type={showNew ? 'text' : 'password'}
              className={`form-control ${pwErrors.password ? 'is-invalid' : ''}`}
              value={pwForm.password}
              onChange={(e) => { setPwForm({ ...pwForm, password: e.target.value }); if (pwErrors.password) setPwErrors((p) => ({ ...p, password: '' })); }}
              placeholder="Min 8 characters"
              style={{ paddingRight: 40 }}
            />
            <button
              type="button"
              onClick={() => setShowNew((v) => !v)}
              style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}
            >
              {showNew ? <FiEyeOff size={16} /> : <FiEye size={16} />}
            </button>
          </div>
          {pwErrors.password && <div className="invalid-feedback">{pwErrors.password}</div>}
        </div>
        <div className="form-group">
          <label className="form-label">Confirm New Password <span className="req">*</span></label>
          <div style={{ position: 'relative' }}>
            <input
              type={showConfirm ? 'text' : 'password'}
              className={`form-control ${pwErrors.password_confirmation ? 'is-invalid' : ''}`}
              value={pwForm.password_confirmation}
              onChange={(e) => { setPwForm({ ...pwForm, password_confirmation: e.target.value }); if (pwErrors.password_confirmation) setPwErrors((p) => ({ ...p, password_confirmation: '' })); }}
              placeholder="Re-enter new password"
              style={{ paddingRight: 40 }}
            />
            <button
              type="button"
              onClick={() => setShowConfirm((v) => !v)}
              style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}
            >
              {showConfirm ? <FiEyeOff size={16} /> : <FiEye size={16} />}
            </button>
          </div>
          {pwErrors.password_confirmation && <div className="invalid-feedback">{pwErrors.password_confirmation}</div>}
        </div>
      </Modal>
    </header>
  );
}